import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "@/index.css";
import App from "@/App";

// Register the service worker so Web Push notifications work when the PWA is
// installed on the user's home screen (iOS 16.4+ and Android). Silent if the
// browser doesn't support SW (e.g. old iOS Safari without PWA install).
//
// Auto-update on redeploy: after a new sw.js is detected (byte-diff → version
// constant bumped), the SW calls `skipWaiting()` + `clients.claim()`. The
// `controllerchange` listener below then reloads the page automatically so the
// user always runs the freshest bundle — no manual cache clear required.
if ("serviceWorker" in navigator) {
  let refreshing = false;
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    // Guard against the initial navigation triggering an infinite reload loop.
    if (refreshing) return;
    refreshing = true;
    window.location.reload();
  });
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js").then((reg) => {
      // Periodically ask the browser to re-fetch sw.js so long-lived tabs
      // (phones left open in the background) pick up new deploys within
      // ~10 minutes instead of the default 24h.
      setInterval(() => { try { reg.update(); } catch (_) {} }, 10 * 60 * 1000);
      // Nudge the waiting SW to activate immediately.
      if (reg.waiting) reg.waiting.postMessage({ type: "SKIP_WAITING" });
      reg.addEventListener("updatefound", () => {
        const nw = reg.installing;
        if (!nw) return;
        nw.addEventListener("statechange", () => {
          if (nw.state === "installed" && navigator.serviceWorker.controller) {
            nw.postMessage({ type: "SKIP_WAITING" });
          }
        });
      });
    }).catch(() => {});
  });
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  },
});

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </React.StrictMode>,
);
